// API keys — company keys managed from the attendance Settings drawer (the
// attendance module grant is the gate; no finer verb exists). POST api-keys
// creates one from a name + optional description and is the ONLY response
// that carries the plain key (only its hash is stored — the list never shows
// it). POST api-keys/list pages newest first and reports the total on page 1
// only. bulk-revoke takes active keys only, bulk-delete revoked keys only;
// either rejects the whole request if any id is in the wrong state. The UI
// acts on one key at a time (the row kebab), sending one-element arrays.

import { baseApi } from '../baseApi';
import { ROUTES } from '../routes';

export enum ApiKeyStatus {
  Active = 'active',
  Revoked = 'revoked',
}

export interface ApiKeyItem {
  id: string;
  name: string;
  /** What the key is for; null when none was given. */
  description: string | null;
  status: ApiKeyStatus;
  /** ISO. */
  createdAt: string;
  /** Creator's full name, null when unknown. */
  createdBy: string | null;
  /** ISO; null while active. */
  revokedAt: string | null;
  revokedBy: string | null;
}

export interface ApiKeysPage {
  items: ApiKeyItem[];
  /** Page 1 only (feed useStickyTotal). */
  total: number | undefined;
}

export interface CreateApiKeyArgs {
  name: string;
  /** Omitted when left blank — the list then reports null. */
  description?: string;
}

/** The create response — `key` is shown once and never again. */
export interface CreatedApiKey {
  id: string;
  name: string;
  key: string;
}

// ---- raw shapes (per the confirmed response) ----
interface ApiApiKey {
  _id: string;
  name: string;
  description: string | null;
  status: ApiKeyStatus;
  createdAt: string;
  createdBy: string | null;
  revokedAt: string | null;
  revokedBy: string | null;
}
interface ApiApiKeysPage {
  items: ApiApiKey[];
  totalRecords?: number;
}
interface ApiCreatedApiKey {
  _id: string;
  name: string;
  key: string;
}

export const toApiKeyItem = (k: ApiApiKey): ApiKeyItem => ({
  id: k._id,
  name: k.name,
  description: k.description,
  status: k.status,
  createdAt: k.createdAt,
  createdBy: k.createdBy,
  revokedAt: k.revokedAt,
  revokedBy: k.revokedBy,
});

export const toApiKeysPage = (res: ApiApiKeysPage): ApiKeysPage => ({
  items: res.items.map(toApiKeyItem),
  total: res.totalRecords,
});

export const toCreatedApiKey = (res: ApiCreatedApiKey): CreatedApiKey => ({
  id: res._id,
  name: res.name,
  key: res.key,
});

const LIST = [{ type: 'ApiKeys' as const, id: 'LIST' }];

export const apiKeysApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getApiKeys: build.query<ApiKeysPage, { page: number; limit: number }>({
      query: (body) => ({ ...ROUTES.apiKeys.list, data: body }),
      transformResponse: toApiKeysPage,
      providesTags: LIST,
    }),
    createApiKey: build.mutation<CreatedApiKey, CreateApiKeyArgs>({
      query: (body) => ({ ...ROUTES.apiKeys.create, data: body }),
      transformResponse: toCreatedApiKey,
      invalidatesTags: LIST,
    }),
    bulkRevokeApiKeys: build.mutation<unknown, string[]>({
      query: (ids) => ({ ...ROUTES.apiKeys.bulkRevoke, data: { ids } }),
      invalidatesTags: LIST,
    }),
    bulkDeleteApiKeys: build.mutation<unknown, string[]>({
      query: (ids) => ({ ...ROUTES.apiKeys.bulkDelete, data: { ids } }),
      invalidatesTags: LIST,
    }),
  }),
});

export const {
  useGetApiKeysQuery,
  useCreateApiKeyMutation,
  useBulkRevokeApiKeysMutation,
  useBulkDeleteApiKeysMutation,
} = apiKeysApi;
